import {
  QuestionFormValues,
  questionSchema,
} from "./useCreateEditQuestionnaireModalForm";
import {
  BookerByIdDocument,
  EnumSurveyQuestionType,
  EnumSurveyQuestionBusinessScope,
  EnumSurveyQuestionWhereaboutsSearchEntityType,
  SurveyManyDocument,
  useSurveyQuestionUpdateMutation,
} from "gql/generated";
import { useFormHandler } from "shared/hooks";
import { useCallback, useMemo } from "react";
import { createMultilingual } from "shared/utils";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";

type useCreateEditQuestionFormOptions = {
  onSuccess?: (quesiton: QuestionFormValues) => void;
  onError?: () => void;
  question: QuestionFormValues | null;
  /** Toggle a new question starts out as a follow-up of. */
  defaultParentQuestion?: string;
};

const questionTypeSupportsOptions = (type?: EnumSurveyQuestionType | string) =>
  type === EnumSurveyQuestionType.Select ||
  type === EnumSurveyQuestionType.Boolean;

export const useCreateEditQuestionForm = ({
  onError,
  onSuccess,
  question: defaultQuestion,
  defaultParentQuestion,
}: useCreateEditQuestionFormOptions) => {
  const defaultValues: QuestionFormValues = useMemo(
    () => ({
      _id: defaultQuestion?._id ?? "",
      isActive: defaultQuestion?.isActive ?? false,
      name: defaultQuestion?.name ?? "",
      type: defaultQuestion?.type ?? EnumSurveyQuestionType.ShortText,
      required: defaultQuestion?.required ?? false,
      whereaboutsSearchEntityType: defaultQuestion?.whereaboutsSearchEntityType,
      description: defaultQuestion?.description,
      parentQuestion: defaultQuestion
        ? defaultQuestion.parentQuestion
        : defaultParentQuestion,
      businessScope: defaultQuestion?.businessScope,
      options: defaultQuestion?.options
        ? defaultQuestion.options
            .filter(op => op?.isActive) // Only show the active options
            .map((op, i) => ({
              _id: op?._id ?? i.toString(),
              isActive: op?.isActive ?? false,
              label: op?.label ?? "",
            }))
        : [],
    }),
    [
      defaultQuestion?._id,
      defaultQuestion?.isActive,
      defaultQuestion?.name,
      defaultQuestion?.options,
      defaultQuestion?.required,
      defaultQuestion?.type,
      defaultQuestion?.whereaboutsSearchEntityType,
      defaultQuestion?.description,
      defaultQuestion?.parentQuestion,
      defaultQuestion?.businessScope,
      defaultParentQuestion,
    ],
  );

  const isEditingQuestion =
    !!defaultQuestion && defaultQuestion._id.length === 24;

  const [updateQuestion] = useSurveyQuestionUpdateMutation({
    refetchQueries: [BookerByIdDocument, SurveyManyDocument],
    awaitRefetchQueries: true,
  });

  return useFormHandler<QuestionFormValues>(
    useCallback(
      async (data, formState) => {
        if (data && formState.isDirty) {
          const questionData = data as QuestionFormValues;
          try {
            // If the question is being edited, update that question in the DB
            if (isEditingQuestion) {
              await updateQuestion({
                variables: {
                  id: defaultQuestion?._id ? defaultQuestion._id : undefined,
                  record: {
                    name: createMultilingual(questionData.name),
                    type: questionData.type as EnumSurveyQuestionType,
                    required:
                      isSurveyDesignFieldType(questionData.type) ||
                      questionData.type === EnumSurveyQuestionType.Toggle
                        ? false
                        : questionData.required,
                    description: isSurveyDesignFieldType(questionData.type)
                      ? undefined
                      : createMultilingual(questionData.description ?? ""),
                    options: questionTypeSupportsOptions(questionData.type)
                      ? questionData.options.map(op => ({
                          _id: op._id.length === 24 ? op._id : undefined,
                          isActive: op.isActive,
                          label: createMultilingual(op.label),
                        }))
                      : undefined,
                    whereaboutsSearchEntityType:
                      questionData.type ===
                      EnumSurveyQuestionType.WhereaboutsSearch
                        ? (questionData.whereaboutsSearchEntityType as EnumSurveyQuestionWhereaboutsSearchEntityType)
                        : undefined,
                    businessScope:
                      questionData.type === EnumSurveyQuestionType.Toggle
                        ? ((questionData.businessScope ??
                            "SHARED") as EnumSurveyQuestionBusinessScope)
                        : null,
                  },
                },
              });
            }

            // If the question is being created, we'll pass the question data
            // to the parent component to manage creating the question in the DB.
            // The reason we don't create the question here is because in case
            // the user chooses to cancel the survey or the question, we don't
            // want to have a number of orphaned questions in the DB.
            onSuccess?.(questionData);
          } catch {
            onError?.();
          }
        }
      },
      [
        isEditingQuestion,
        onSuccess,
        updateQuestion,
        defaultQuestion?._id,
        onError,
      ],
    ),
    defaultValues,
    { schema: questionSchema, mode: "onBlur" },
  );
};
